import { useEffect, useState } from "react";
import { X } from "lucide-react";
import { RUNTIME_ERROR_POLL_MS, runtimePollDelay } from "./runtime-polling";

type Operation = {
  id: string;
  method: string;
  action: string;
  started_at: string;
  elapsedMs: number;
};

const labels: Record<string, string> = {
  portfolio_state: "计算完整家庭资产视图",
  page_state: "计算当前页面数据",
  page_model: "读取当前页面数据",
  sync_data_sources: "同步外部数据源",
  check_data_freshness: "检查数据更新时间",
  sync_bank_wealth_products: "同步银行理财数据",
  sync_platform_exchange_rates: "同步汇率",
  sync_mailbox_messages: "同步邮箱账单",
  refresh_imported_positions: "刷新持仓价格",
  recalculate_investment_history: "重新计算投资历史",
};

export function RuntimeActivity({ enabled }: { enabled: boolean }) {
  const [operations, setOperations] = useState<Operation[]>([]);
  const [dismissed, setDismissed] = useState(false);
  const [primaryReady, setPrimaryReady] = useState(() => typeof performance !== "undefined"
    && performance.getEntriesByName("qy:primary-ready", "mark").length > 0);

  useEffect(() => {
    if (!enabled || primaryReady) return undefined;
    const ready = () => setPrimaryReady(true);
    window.addEventListener("qy:primary-ready", ready, { once: true });
    return () => window.removeEventListener("qy:primary-ready", ready);
  }, [enabled, primaryReady]);

  useEffect(() => {
    if (!enabled || !primaryReady) return undefined;
    let stopped = false;
    let timer = 0;
    let idlePollCount = 0;
    let controller: AbortController | null = null;

    function schedule(delay: number) {
      window.clearTimeout(timer);
      if (!stopped && document.visibilityState === "visible") timer = window.setTimeout(poll, delay);
    }

    async function poll() {
      if (stopped || document.visibilityState !== "visible") return;
      controller?.abort();
      controller = new AbortController();
      try {
        const response = await fetch("/api/runtime/status", {
          cache: "no-store",
          credentials: "include",
          signal: controller.signal,
        });
        if (response.status === 401 || response.status === 403) {
          if (!stopped) setOperations([]);
          return;
        }
        if (response.ok) {
          const payload = await response.json() as { active?: Operation[] };
          const active = payload.active ?? [];
          if (!stopped) {
            setOperations(active);
            if (!active.length) setDismissed(false);
          }
          const delay = runtimePollDelay(active.length, idlePollCount);
          idlePollCount = active.length ? 0 : idlePollCount + 1;
          schedule(delay);
          return;
        }
      } catch (error) {
        if (error instanceof DOMException && error.name === "AbortError") return;
        if (!stopped) setOperations([]);
      }
      schedule(RUNTIME_ERROR_POLL_MS);
    }

    function handleVisibilityChange() {
      window.clearTimeout(timer);
      if (document.visibilityState === "hidden") {
        controller?.abort();
        return;
      }
      idlePollCount = 0;
      void poll();
    }

    document.addEventListener("visibilitychange", handleVisibilityChange);
    void poll();
    return () => {
      stopped = true;
      controller?.abort();
      window.clearTimeout(timer);
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, [enabled, primaryReady]);

  if (!enabled || !primaryReady || !operations.length || dismissed) return null;
  return <aside className="qy-runtime-activity" aria-live="polite" aria-label="服务器当前处理状态">
    <header><strong>服务器正在处理</strong><button type="button" onClick={() => setDismissed(true)} aria-label="关闭服务器处理状态" title="关闭"><X size={16} /></button></header>
    {operations.slice(0, 3).map((operation) => <div className="qy-runtime-activity-row" key={operation.id}>
      <span>{labels[operation.action] ?? operation.action}</span>
      <small>{(operation.elapsedMs / 1000).toFixed(1)} 秒 · {operation.id.slice(0, 8)}</small>
    </div>)}
  </aside>;
}
